import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, type Attribute } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, Checkbox, ConfirmDialog, EmptyState, Field, Input, Notice, Spinner } from '../ui/controls'

/**
 * Справочник атрибутов (п. 4.4).
 *
 * Список с поиском по коду и названию, создание и правка в боковой панели,
 * удаление с проверкой заполненных значений.
 */
export default function AttributesPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.catalog

  const [list, setList] = useState<Attribute[] | null>(null)
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<Attribute | 'new' | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)

  const reload = useCallback(async (q: string) => {
    try {
      setList(await api.attributes(q))
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить' })
      setList([])
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => void reload(query), 250)
    return () => clearTimeout(t)
  }, [query, reload])

  if (list === null) return <Spinner label="Загрузка справочника атрибутов…" />

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <section>
        <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Атрибуты</h2>
            <p className="mt-0.5 text-sm text-ink-500">
              Характеристики товаров. Привязываются к категориям и наследуются вниз по дереву.
            </p>
          </div>
          {canWrite && (
            <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
              + Атрибут
            </Button>
          )}
        </header>

        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по коду и названию"
          aria-label="Поиск атрибутов"
          className="mb-3"
        />

        {notice && (
          <Notice kind={notice.kind} onClose={() => setNotice(null)}>
            {notice.text}
          </Notice>
        )}

        {list.length === 0 ? (
          <EmptyState
            title={query ? 'Ничего не найдено' : 'Справочник атрибутов пуст'}
            hint={
              query
                ? 'Измените запрос или очистите поиск.'
                : 'Атрибут — это именованная характеристика: цвет, объём, напряжение. Заведите первый и привяжите его к категории.'
            }
            action={
              canWrite && !query ? (
                <Button variant="primary" onClick={() => setEditing('new')}>
                  Создать первый атрибут
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
            <table className="w-full min-w-[42rem] text-left text-sm">
              <thead className="border-b border-ink-200 bg-ink-50 text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-900">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Название</th>
                  <th className="px-3 py-2.5 font-medium">Код</th>
                  <th className="px-3 py-2.5 font-medium">Тип</th>
                  <th className="px-3 py-2.5 font-medium">Фасет</th>
                  <th className="px-3 py-2.5 text-right font-medium">Категорий</th>
                  <th className="px-3 py-2.5 text-right font-medium">Товаров</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                {list.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => setEditing(a)}
                    className={`cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-900 ${
                      editing !== 'new' && editing?.id === a.id ? 'bg-brand-50 dark:bg-brand-500/15' : ''
                    }`}
                  >
                    <td className="px-3 py-2.5 text-ink-800 dark:text-ink-200">
                      {a.name_ru}
                      {a.unit && <span className="ml-1 text-ink-400">, {a.unit}</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-ink-500">{a.code}</td>
                    <td className="px-3 py-2.5 text-ink-600 dark:text-ink-400">
                      {a.value_type_title}
                    </td>
                    <td className="px-3 py-2.5">
                      {a.in_facets ? (
                        <span className="rounded-full bg-success-100 dark:bg-success-700/25 px-2 py-0.5 text-xs text-success-700 dark:text-success-100">
                          да
                        </span>
                      ) : (
                        <span className="text-xs text-ink-400">нет</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-ink-600 dark:text-ink-400">
                      {a.categories}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-ink-600 dark:text-ink-400">
                      {a.products}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        {editing ? (
          <AttributeForm
            key={editing === 'new' ? 'new' : editing.id}
            attribute={editing === 'new' ? undefined : editing}
            canWrite={canWrite}
            onSaved={async (msg) => {
              setEditing(null)
              await reload(query)
              setNotice({ kind: msg.warning ? 'warning' : 'success', text: msg.text })
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <EmptyState
            title="Атрибут не выбран"
            hint="Выберите атрибут в списке, чтобы изменить его, либо создайте новый."
          />
        )}
      </section>
    </div>
  )
}

function AttributeForm({
  attribute,
  canWrite,
  onSaved,
  onCancel,
}: {
  attribute?: Attribute
  canWrite: boolean
  onSaved: (msg: { text: string; warning?: boolean }) => void | Promise<void>
  onCancel: () => void
}) {
  const [nameRu, setNameRu] = useState(attribute?.name_ru ?? '')
  const [code, setCode] = useState(attribute?.code ?? '')
  const [unit, setUnit] = useState(attribute?.unit ?? '')
  const [inFacets, setInFacets] = useState(attribute?.in_facets ?? false)
  const [showInCard, setShowInCard] = useState(attribute?.show_in_card ?? true)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [categories, setCategories] = useState<{ id: number; name_ru: string }[] | null>(null)

  // Перечень категорий, к которым привязан атрибут (А-305).
  useEffect(() => {
    if (!attribute) return
    void api
      .attributeCategories(attribute.id)
      .then(setCategories)
      .catch(() => setCategories([]))
  }, [attribute])

  const codeChanged = attribute !== undefined && code !== attribute.code

  return (
    <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-800">
      <h3 className="mb-4 text-sm font-semibold text-ink-900 dark:text-ink-50">
        {attribute ? 'Изменение атрибута' : 'Новый атрибут'}
      </h3>

      {error && <Notice kind="error">{error}</Notice>}
      {codeChanged && (
        <Notice kind="warning">
          Изменение кода потребует полной переиндексации каталога. До неё фильтры витрины
          по этому атрибуту работать не будут.
        </Notice>
      )}

      <div className="space-y-4">
        <Field label="Название (рус)">
          <Input
            value={nameRu}
            onChange={(e) => setNameRu(e.target.value)}
            disabled={!canWrite || pending}
            required
          />
        </Field>

        <Field
          label="Код"
          hint="Строчные латинские буквы, цифры и подчёркивания. Если оставить пустым, будет предложен по названию."
        >
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={!canWrite || pending}
            placeholder="например, shell_material"
            className="font-mono"
          />
        </Field>

        <Field label="Единица измерения" hint="Отображается после значения. Необязательно.">
          <Input
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={!canWrite || pending}
            placeholder="например, л"
          />
        </Field>

        <Checkbox
          label="Участвует в фильтрах витрины"
          hint="По значениям атрибута строится фасет в каталоге"
          checked={inFacets}
          onChange={(e) => setInFacets(e.target.checked)}
          disabled={!canWrite || pending}
        />

        <Checkbox
          label="Показывать в карточке товара"
          checked={showInCard}
          onChange={(e) => setShowInCard(e.target.checked)}
          disabled={!canWrite || pending}
        />
      </div>

      {attribute && categories !== null && (
        <div className="mt-4 border-t border-ink-200 pt-3 dark:border-ink-800">
          <p className="mb-1.5 text-xs font-medium text-ink-600 dark:text-ink-400">
            Привязан к категориям: {categories.length}
          </p>
          {categories.length > 0 && (
            <p className="text-xs leading-relaxed text-ink-500">
              {categories.map((c) => c.name_ru).join(', ')}
            </p>
          )}
        </div>
      )}

      {canWrite && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={pending || nameRu.trim() === ''}
            onClick={async () => {
              setPending(true)
              setError(null)
              try {
                const body = {
                  name_ru: nameRu.trim(),
                  code: code.trim() || undefined,
                  unit: unit.trim() || null,
                  in_facets: inFacets,
                  show_in_card: showInCard,
                }
                if (attribute) {
                  const res = await api.updateAttribute(attribute.id, body)
                  await onSaved({
                    text: res.warning ?? 'Сохранено',
                    warning: Boolean(res.reindex_required),
                  })
                } else {
                  await api.createAttribute(body)
                  await onSaved({ text: 'Атрибут создан' })
                }
              } catch (e) {
                setError(e instanceof ApiError ? e.message : 'Не удалось сохранить')
              } finally {
                setPending(false)
              }
            }}
          >
            {pending ? 'Сохранение…' : attribute ? 'Сохранить' : 'Создать'}
          </Button>
          <Button size="sm" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          {attribute && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              disabled={pending}
            >
              Удалить
            </Button>
          )}
        </div>
      )}

      {confirmDelete && attribute && (
        <ConfirmDialog
          title="Удалить атрибут?"
          message={
            <>
              Атрибут «{attribute.name_ru}» будет удалён без возможности восстановления.
              {attribute.products > 0 && (
                <>
                  {' '}
                  У {attribute.products} товаров заполнены значения — удаление будет отклонено.
                </>
              )}
            </>
          }
          confirmLabel="Удалить"
          pending={pending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            setPending(true)
            try {
              await api.deleteAttribute(attribute.id)
              setConfirmDelete(false)
              await onSaved({ text: 'Атрибут удалён' })
            } catch (e) {
              setConfirmDelete(false)
              if (e instanceof ApiError) {
                const d = e.details as { products?: number }
                setError(`${e.message}. Товаров со значением: ${d.products ?? 0}.`)
              } else {
                setError('Не удалось удалить')
              }
            } finally {
              setPending(false)
            }
          }}
        />
      )}
    </div>
  )
}
